"use client";

import Image from "next/image";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Sparkles,
  Activity,
  Bell,
  TrendingUp,
  Play,
  ArrowUpRight,
  Flame,
  Timer,
  Waves,
  Layers,
  Zap,
  Gem,
  Banknote,
  Radar,
} from "lucide-react";
import {
  KIND_LABEL,
  type LibraryItem,
  type LibraryKind,
} from "@/lib/mock/library";
import { cn } from "@/lib/utils";

const KIND_ICON: Record<LibraryKind, typeof Sparkles> = {
  skill: Sparkles,
  signal: Activity,
  automation: Bell,
  idea: TrendingUp,
  video: Play,
};

/**
 * Skill names can carry a variant suffix after a colon / dash
 * (e.g. "Ticker Deep Dive: Full Deep Dive"). We show the base name as the
 * title and the variant as a small badge so nothing ever truncates mid-word.
 */
function splitSkillTitle(title: string): { base: string; variant: string | null } {
  const m = title.match(/^(.*?)\s*[:\u2014\u2013]\s*(.+)$/);
  if (!m) return { base: title, variant: null };
  const base = m[1].trim();
  const rest = m[2].trim();
  let variant = rest.toUpperCase();
  if (/quick/i.test(rest)) variant = "QUICK";
  else if (/full/i.test(rest)) variant = "FULL";
  else if (/deep|advisor|pro/i.test(rest)) variant = "DEEP";
  return { base, variant };
}

function hexToRgb(hex: string) {
  const v = hex.replace("#", "");
  const n = parseInt(v, 16);
  return `${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}`;
}

function PricePill({
  label,
  icon,
  price,
}: {
  label: string;
  icon: string;
  price: number;
}) {
  return (
    <div className="flex items-center gap-1.5 rounded-md border border-white/[0.10] bg-white/[0.06] px-2 py-1 backdrop-blur-md">
      <Image
        src={icon}
        alt={label}
        width={14}
        height={14}
        className="h-3.5 w-3.5 rounded-[3px] object-contain"
      />
      <span className="text-[11px] font-semibold tabular-nums text-white">
        ${price.toFixed(2)}
      </span>
    </div>
  );
}

function SkillCard({
  item,
  onSelect,
}: {
  item: LibraryItem;
  onSelect?: () => void;
}) {
  const rgb = hexToRgb(item.accent);
  const base = item.basePrice ?? 0.3;
  const preview = item.previewMd || item.blurb;
  // Real per-model prices; fall back to base (and base*6 only if opus unknown).
  const flashPrice = item.flashPrice ?? base;
  const opusPrice = item.opusPrice ?? base * 6;
  const thumb = item.thumbnailUrl;

  return (
    <motion.button
      onClick={onSelect}
      whileHover={{ y: -4 }}
      transition={{ type: "spring", stiffness: 320, damping: 26 }}
      className="group relative flex h-full w-full flex-col overflow-hidden rounded-xl border border-white/[0.07] bg-[#0c0c0e] text-left shadow-[0_2px_14px_rgba(0,0,0,0.5)] transition-colors duration-300"
    >
      {/* preview header: GPT-5.4 thumbnail when present, else zoomed-out
          markdown. Pricing pills overlaid on either. */}
      <div className="relative aspect-[16/9] w-full overflow-hidden border-b border-white/[0.05] bg-white/[0.02]">
        {/* pricing overlay (Flash + Opus) */}
        <div className="absolute right-3 top-3 z-10 flex items-center gap-1.5">
          <PricePill label="Flash" icon="/models/gemini-v2.png" price={flashPrice} />
          <PricePill label="Opus" icon="/models/claude-v5.png" price={opusPrice} />
        </div>
        {thumb ? (
          <Image
            src={thumb}
            alt={item.title}
            fill
            sizes="(max-width: 1024px) 50vw, 25vw"
            className="object-cover"
          />
        ) : (
          <>
            <div className="h-full px-4 pt-3.5">
              <div className="max-w-[62%] text-[8px] font-bold leading-tight tracking-tight text-white">
                {item.title}
              </div>
              <div className="mt-1 h-px w-7 bg-white/30" />
              <p className="mt-1.5 whitespace-pre-line text-[7.5px] leading-[1.6] text-white/90">
                {preview}
              </p>
            </div>
            {/* fade only the markdown fallback into the card; real thumbnails
                have their own baked title in the lower third and must NOT be
                darkened by this band. */}
            <div className="absolute inset-x-0 bottom-0 h-9 bg-gradient-to-t from-[#0c0c0e] to-transparent" />
          </>
        )}
      </div>

      {/* body — title + one meta row, same proportions as the home-tab cards */}
      <div className="flex flex-1 flex-col gap-2 px-4 pb-3 pt-3">
        {/* title (hero) + variant badge so long names never truncate mid-word */}
        {(() => {
          const { base, variant } = splitSkillTitle(item.title);
          // Emerald = quick/light tier, gold = deep/full premium tier. Colored
          // so the tier badge actually shows (never gray, never translucent).
          const badgeClass =
            variant === "QUICK"
              ? "border-emerald-400/50 bg-emerald-500/20 text-emerald-300"
              : "border-amber-400/50 bg-amber-500/20 text-amber-300";
          return (
            <div className="flex items-center gap-2">
              <h3 className="min-w-0 truncate text-[14.5px] font-semibold leading-snug tracking-tight text-white">
                {base}
              </h3>
              {variant && (
                <span
                  className={`shrink-0 rounded-md border px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${badgeClass}`}
                >
                  {variant}
                </span>
              )}
            </div>
          );
        })()}

        {/* meta row: runtime · runs (left) + Run (right) */}
        <div className="mt-auto flex items-center justify-between">
          <div className="flex min-w-0 items-center gap-2">
            {item.runtimeLabel && (
              <span className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-md border border-emerald-400/30 bg-emerald-500/10 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-300">
                <Timer className="h-3 w-3" />
                {item.runtimeLabel}
              </span>
            )}
            <span className="flex shrink-0 items-center gap-1 text-[11px] font-medium text-white/70">
              <Flame className="h-3 w-3" style={{ color: item.accent }} />
              {item.uses?.toLocaleString()} runs
            </span>
          </div>
          <span className="flex shrink-0 items-center gap-1 text-[11px] font-medium text-white/90 transition-colors group-hover:text-white">
            Run
            <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
          </span>
        </div>
      </div>

      <div
        className="pointer-events-none absolute inset-0 rounded-xl opacity-0 transition-opacity duration-300 group-hover:opacity-100"
        style={{ boxShadow: `inset 0 0 0 1px rgba(${rgb},0.35)` }}
      />
    </motion.button>
  );
}

const CANDLE_UP = "#26a69a"; // tradingview green
const CANDLE_DOWN = "#ef5350"; // tradingview red

type Bar = { o: number; h: number; l: number; c: number };

/**
 * Fetch real daily bars for the featured position's ticker (via the existing
 * /api/polygon/aggregates proxy) from a bit before entry to today — the same
 * window the signal page's Trade chart shows.
 */
function usePositionBars(ticker?: string, openedAt?: string | null) {
  const [bars, setBars] = useState<Bar[] | null>(null);
  useEffect(() => {
    if (!ticker) return;
    let cancelled = false;
    const opened = openedAt ? new Date(openedAt) : new Date();
    const from = new Date(opened.getTime() - 21 * 86400_000);
    const to = new Date();
    const fmt = (d: Date) => d.toISOString().slice(0, 10);
    fetch(
      `/api/polygon/aggregates?ticker=${encodeURIComponent(ticker)}&from=${fmt(from)}&to=${fmt(to)}&timespan=day&multiplier=1`,
    )
      .then((r) => r.json())
      .then((d) => {
        if (cancelled) return;
        const results = Array.isArray(d?.results) ? d.results : [];
        const mapped: Bar[] = results
          .map((b: any) => ({ o: b.o, h: b.h, l: b.l, c: b.c }))
          .filter((b: Bar) => [b.o, b.h, b.l, b.c].every((v) => typeof v === "number"));
        if (mapped.length >= 2) setBars(mapped);
      })
      .catch(() => {
        // fall back to the equity curve silently
      });
    return () => {
      cancelled = true;
    };
  }, [ticker, openedAt]);
  return bars;
}

/**
 * Real candlestick chart for the featured position — TradingView-style green/
 * red candles from actual daily bars, with a dotted entry line. Mirrors the
 * Trade chart on the signal page's Current Signal(s) tab.
 */
function MiniTradeChart({ bars, entry }: { bars: Bar[]; entry: number | null }) {
  const W = 240;
  const H = 84;
  const padY = 5;
  const padX = 2;

  const hi = Math.max(...bars.map((b) => b.h), entry ?? -Infinity);
  const lo = Math.min(...bars.map((b) => b.l), entry ?? Infinity);
  const span = hi - lo || 1;
  const y = (v: number) => padY + (1 - (v - lo) / span) * (H - padY * 2);

  const n = bars.length;
  const slot = (W - padX * 2) / n;
  const bodyW = Math.max(1.5, Math.min(6, slot * 0.62));

  const gridYs = [0.25, 0.5, 0.75].map((f) => padY + f * (H - padY * 2));

  return (
    <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="h-full w-full">
      {gridYs.map((gy, i) => (
        <line key={`h${i}`} x1="0" y1={gy} x2={W} y2={gy} stroke="#ffffff" strokeOpacity="0.05" strokeWidth="1" />
      ))}
      {entry != null && (
        <line
          x1="0"
          y1={y(entry)}
          x2={W}
          y2={y(entry)}
          stroke="#ffffff"
          strokeOpacity="0.55"
          strokeWidth="1"
          strokeDasharray="3 3"
          vectorEffect="non-scaling-stroke"
        />
      )}
      {bars.map((b, i) => {
        const cx = padX + slot * (i + 0.5);
        const color = b.c >= b.o ? CANDLE_UP : CANDLE_DOWN;
        const top = Math.min(y(b.o), y(b.c));
        const h = Math.max(1, Math.abs(y(b.c) - y(b.o)));
        return (
          <g key={i}>
            <line
              x1={cx}
              y1={y(b.h)}
              x2={cx}
              y2={y(b.l)}
              stroke={color}
              strokeWidth="1"
              vectorEffect="non-scaling-stroke"
            />
            <rect x={cx - bodyW / 2} y={top} width={bodyW} height={h} fill={color} rx="0.5" />
          </g>
        );
      })}
    </svg>
  );
}

/**
 * Mini equity-curve area chart — the same visual as the Performance tab's
 * chart (smooth green line + gradient fill + dotted baseline at the deployed
 * amount), scaled down for the card thumbnail. Pure SVG, real curve data.
 */
function MiniEquityChart({
  curve,
  baseline,
  color,
  gradId,
}: {
  curve: number[];
  baseline: number;
  color: string;
  gradId: string;
}) {
  const W = 240;
  const H = 70;
  const padY = 6;

  if (!curve || curve.length < 2) {
    return (
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="h-full w-full">
        <line x1="0" y1={H / 2} x2={W} y2={H / 2} stroke="#3f3f46" strokeWidth="1" />
      </svg>
    );
  }

  const lo = Math.min(...curve, baseline);
  const hi = Math.max(...curve, baseline);
  const span = hi - lo || 1;
  const x = (i: number) => (i / (curve.length - 1)) * W;
  const y = (v: number) => padY + (1 - (v - lo) / span) * (H - padY * 2);

  const line = curve
    .map((v, i) => `${i === 0 ? "M" : "L"}${x(i).toFixed(1)},${y(v).toFixed(1)}`)
    .join(" ");
  const area = `${line} L${W},${H} L0,${H} Z`;

  // Faint grid lines (same vibe as the Performance chart).
  const gridYs = [0.25, 0.5, 0.75].map((f) => padY + f * (H - padY * 2));

  return (
    <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="h-full w-full">
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.35" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      {gridYs.map((gy, i) => (
        <line key={`h${i}`} x1="0" y1={gy} x2={W} y2={gy} stroke="#ffffff" strokeOpacity="0.05" strokeWidth="1" />
      ))}
      {/* dotted baseline at the deployed amount */}
      <line
        x1="0"
        y1={y(baseline)}
        x2={W}
        y2={y(baseline)}
        stroke={color}
        strokeOpacity="0.45"
        strokeWidth="1"
        strokeDasharray="3 3"
        vectorEffect="non-scaling-stroke"
      />
      <path d={area} fill={`url(#${gradId})`} />
      <path
        d={line}
        fill="none"
        stroke={color}
        strokeWidth="1.5"
        vectorEffect="non-scaling-stroke"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  );
}

/**
 * Per-signal visual identity so the cards stop looking like clones. The
 * strategy is inferred from the signal name; each gets its own accent color
 * + icon for the SIGNAL chip, glow, and hover ring. The candle chart still
 * uses green/red (up/down) so the real track record reads honestly.
 */
function signalTheme(title: string): { accent: string; Icon: typeof Activity; tag: string } {
  const t = (title || "").toLowerCase();
  if (t.includes("dark pool")) return { accent: "#a78bfa", Icon: Layers, tag: "Dark Pool" };
  if (t.includes("whale")) return { accent: "#38bdf8", Icon: Waves, tag: "Whale Flow" };
  if (t.includes("earnings") || t.includes("iv")) return { accent: "#f59e0b", Icon: Zap, tag: "Earnings" };
  if (t.includes("insider")) return { accent: "#34d399", Icon: Gem, tag: "Insider" };
  if (t.includes("dividend") || t.includes("cash")) return { accent: "#10b981", Icon: Banknote, tag: "Income" };
  if (t.includes("momentum") || t.includes("swing")) return { accent: "#fb7185", Icon: TrendingUp, tag: "Momentum" };
  return { accent: "#22d3ee", Icon: Radar, tag: "Signal" };
}

function SignalCard({
  item,
  onSelect,
}: {
  item: LibraryItem;
  onSelect?: () => void;
}) {
  const theme = signalTheme(item.title);
  const rgb = hexToRgb(theme.accent);
  const StratIcon = theme.Icon;
  const s = item.signal!;
  const featured = s.featured ?? null;
  const bars = usePositionBars(featured?.ticker, featured?.openedAt);
  const up = s.allTimePct >= 0;
  const pct = `${up ? "+" : ""}${s.allTimePct.toFixed(1)}%`;
  const winPct = `${Math.round(s.winRate * 100)}%`;
  const accent = up ? "#34d399" : "#f87171";

  // Use the REAL book: capital deployed → current value (deployed + profit).
  const deployed = s.deployedUsd || 0;
  const profit = s.profitUsd || 0;
  const now = deployed + profit;
  // Map the equity curve's shape onto the real deployed → now range.
  const c0 = s.curve.length ? s.curve[0] : 1;
  const scaled = s.curve.length
    ? s.curve.map((v) => (v / (c0 || 1)) * deployed)
    : [deployed, now];
  const fmtMoney = (n: number) =>
    n >= 1_000_000
      ? `$${(n / 1_000_000).toFixed(2)}M`
      : n >= 100_000
        ? `$${Math.round(n / 1000)}K`
        : n >= 1_000
          ? `$${(n / 1000).toFixed(1)}K`
          : `$${Math.round(n)}`;

  return (
    <motion.button
      onClick={onSelect}
      whileHover={{ y: -4 }}
      transition={{ type: "spring", stiffness: 320, damping: 26 }}
      className="group relative flex h-full w-full flex-col overflow-hidden rounded-xl border border-white/[0.07] bg-[#0c0c0e] text-left shadow-[0_2px_14px_rgba(0,0,0,0.5)] transition-colors duration-300"
    >
      {/* "Current Signal(s)" thumbnail: the newest open position exactly like
          the signal page — name + LONG chip + PnL, entry/current/target/stop
          boxes, and the REAL candlestick trade chart with the entry line. */}
      <div className="relative aspect-[16/9] w-full overflow-hidden border-b border-white/[0.05] bg-[#0a0a0b]">
        {featured ? (
          <div className="relative flex h-full flex-col">
            {/* header row — position name + LONG/SHORT chip, PnL on the right */}
            <div className="flex items-center justify-between gap-2 px-2.5 pt-2">
              <div className="flex min-w-0 items-center gap-1.5">
                <span className="truncate text-[11px] font-bold leading-tight text-white">
                  {featured.name || `${featured.ticker} ${item.title}`}
                </span>
                <span
                  className={cn(
                    "shrink-0 rounded px-1 py-px text-[8px] font-bold uppercase tracking-wider",
                    featured.direction === "short"
                      ? "bg-rose-500/20 text-rose-300"
                      : "bg-emerald-500/20 text-emerald-300",
                  )}
                >
                  {featured.direction}
                </span>
              </div>
              {featured.pnlPct != null && (
                <span
                  className={cn(
                    "shrink-0 text-[11px] font-bold tabular-nums",
                    featured.pnlPct >= 0 ? "text-emerald-400" : "text-rose-400",
                  )}
                >
                  {featured.pnlPct >= 0 ? "+" : ""}
                  {featured.pnlPct.toFixed(1)}%
                </span>
              )}
            </div>

            {/* stat boxes — ENTRY / CURRENT / TARGET / STOP, like the page */}
            <div className="mt-1.5 grid grid-cols-4 gap-1 px-2.5">
              {(
                [
                  ["Entry", featured.entry, "text-white"],
                  ["Current", featured.currentPrice, "text-white"],
                  ["Target", featured.takeProfit, "text-emerald-400"],
                  [
                    featured.trailPct
                      ? `Stop (${Math.round(featured.trailPct * 100)}%)`
                      : "Stop",
                    featured.stop,
                    "text-rose-400",
                  ],
                ] as Array<[string, number | null, string]>
              ).map(([label, value, valueClass]) => (
                <div
                  key={label}
                  className="rounded border border-white/[0.07] bg-white/[0.03] px-1.5 py-1"
                >
                  <div className="truncate text-[6.5px] font-semibold uppercase tracking-wider text-white/40">
                    {label}
                  </div>
                  <div className={cn("mt-px text-[9.5px] font-bold tabular-nums", valueClass)}>
                    {value != null ? `$${value.toFixed(2)}` : "—"}
                  </div>
                </div>
              ))}
            </div>

            {/* trade chart fills the rest — real bars, dotted entry line */}
            <div className="relative mt-1 min-h-0 flex-1">
              {bars ? (
                <MiniTradeChart bars={bars} entry={featured.entry} />
              ) : (
                <MiniEquityChart
                  curve={scaled}
                  baseline={deployed}
                  color={accent}
                  gradId={`sig-grad-${item.id}`}
                />
              )}
            </div>
          </div>
        ) : (
          /* no open positions — fall back to the equity curve + headline */
          <div className="relative flex h-full flex-col">
            <div className="flex items-start justify-between gap-2 px-3 pt-2.5">
              <div className="min-w-0">
                <div className="truncate text-[8px] font-semibold uppercase tracking-wider text-white/45">
                  {fmtMoney(deployed)} deployed is now
                </div>
                <div className="mt-0.5 flex items-baseline gap-1.5">
                  <span className="text-lg font-extrabold tabular-nums leading-none text-white">
                    {fmtMoney(now)}
                  </span>
                  <span
                    className="rounded border px-1 py-px text-[9px] font-bold tabular-nums"
                    style={{
                      color: accent,
                      borderColor: `${accent}4d`,
                      backgroundColor: `${accent}1a`,
                    }}
                  >
                    {pct}
                  </span>
                </div>
              </div>
              <div className="flex shrink-0 items-start gap-3 pt-0.5 text-right">
                <div>
                  <div className="text-[7.5px] font-semibold uppercase tracking-wider text-white/40">
                    Win rate
                  </div>
                  <div className="mt-0.5 text-[11px] font-bold tabular-nums text-white">
                    {winPct}
                  </div>
                </div>
              </div>
            </div>
            <div className="relative mt-1 min-h-0 flex-1">
              <MiniEquityChart
                curve={scaled}
                baseline={deployed}
                color={accent}
                gradId={`sig-grad-${item.id}`}
              />
            </div>
          </div>
        )}

        {/* strategy chip stays as the signal's identity marker */}
        <div className="absolute right-2 bottom-1.5">
          <span
            className="flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[8.5px] font-semibold uppercase tracking-wider backdrop-blur-md"
            style={{
              color: theme.accent,
              borderColor: `rgba(${rgb},0.35)`,
              backgroundColor: "rgba(0,0,0,0.55)",
            }}
          >
            <StratIcon className="h-2.5 w-2.5 shrink-0" />
            <span>{theme.tag}</span>
          </span>
        </div>
      </div>

      {/* body — title + one stat row, same proportions as the home-tab cards */}
      <div className="flex flex-1 flex-col gap-2 px-4 pb-3 pt-3">
        <h3 className="truncate text-[14.5px] font-semibold leading-snug tracking-tight text-white">
          {item.title}
        </h3>

        {/* stat strip — single meta row pinned to the bottom */}
        <div className="mt-auto flex items-center gap-4">
          {[
            ["Deployed", fmtMoney(deployed)],
            ["Trades", String(s.totalTrades)],
            ["Open", String(s.openTrades)],
          ].map(([label, value]) => (
            <div key={label} className="flex items-baseline gap-1">
              <span className="text-[12px] font-semibold tabular-nums text-white/85">
                {value}
              </span>
              <span className="text-[9.5px] font-medium uppercase tracking-wide text-white/40">
                {label}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div
        className="pointer-events-none absolute inset-0 rounded-xl opacity-0 transition-opacity duration-300 group-hover:opacity-100"
        style={{ boxShadow: `inset 0 0 0 1px rgba(${rgb},0.35)` }}
      />
    </motion.button>
  );
}

function VideoCard({
  item,
  onSelect,
}: {
  item: LibraryItem;
  onSelect?: () => void;
}) {
  const rgb = hexToRgb(item.accent);
  const thumb = item.thumbnailUrl;
  const isSoon = item.comingSoon;
  const isFeatured = Boolean(item.featured);

  return (
    <motion.button
      onClick={isSoon ? undefined : onSelect}
      disabled={isSoon}
      whileHover={isSoon ? undefined : { y: -4 }}
      transition={{ type: "spring", stiffness: 320, damping: 26 }}
      className={cn(
        "group relative flex h-full w-full flex-col overflow-hidden rounded-xl border bg-[#0c0c0e] text-left shadow-[0_2px_14px_rgba(0,0,0,0.5)] transition-colors duration-300",
        isFeatured
          ? "border-emerald-400/45 shadow-[0_0_28px_-6px_rgba(16,185,129,0.55)]"
          : "border-white/[0.07]",
        isSoon ? "cursor-default opacity-70" : "",
      )}
    >
      <div className="relative aspect-[16/9] w-full overflow-hidden border-b border-white/[0.05] bg-[#0a0a0b]">
        {thumb ? (
          <>
            <Image
              src={thumb}
              alt={item.title}
              fill
              sizes="(max-width: 1024px) 50vw, 25vw"
              className="object-cover"
            />
            {!isSoon && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/25 transition-colors group-hover:bg-black/35">
                <div
                  className="flex h-12 w-12 items-center justify-center rounded-full border transition-transform duration-300 group-hover:scale-110"
                  style={{
                    borderColor: `rgba(${rgb},0.45)`,
                    backgroundColor: `rgba(${rgb},0.92)`,
                  }}
                >
                  <Play className="ml-0.5 h-5 w-5 text-white" fill="white" />
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            <div
              className="absolute inset-0"
              style={{
                background: `radial-gradient(120% 120% at 0% 0%, rgba(${rgb},0.12), transparent 55%), radial-gradient(130% 130% at 100% 100%, rgba(${rgb},0.08), transparent 50%), #0a0a0b`,
              }}
            />
            <div
              className="absolute inset-0 opacity-[0.06]"
              style={{
                backgroundImage:
                  "linear-gradient(to right, #fff 1px, transparent 1px), linear-gradient(to bottom, #fff 1px, transparent 1px)",
                backgroundSize: "26px 26px",
              }}
            />
            <div className="absolute inset-0 flex items-center justify-center px-6 text-center">
              {isSoon ? (
                <span className="text-sm font-medium text-white/50">More coming soon</span>
              ) : (
                <div
                  className="flex h-12 w-12 items-center justify-center rounded-full border transition-transform duration-500 group-hover:scale-110"
                  style={{
                    borderColor: `rgba(${rgb},0.30)`,
                    backgroundColor: `rgba(${rgb},0.10)`,
                  }}
                >
                  <Play className="h-5 w-5" style={{ color: item.accent }} fill={item.accent} />
                </div>
              )}
            </div>
          </>
        )}
        {!isSoon && (
          <div className="absolute left-2.5 top-2">
            {isFeatured ? (
              <span className="rounded-md border border-emerald-300/50 bg-emerald-500/25 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-50 shadow-[0_0_12px_rgba(16,185,129,0.5)] backdrop-blur-md">
                Start here
              </span>
            ) : (
              <span
                className="rounded-md border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider backdrop-blur-md"
                style={{
                  color: item.accent,
                  borderColor: `rgba(${rgb},0.35)`,
                  backgroundColor: "rgba(0,0,0,0.55)",
                }}
              >
                Tutorial
              </span>
            )}
          </div>
        )}
      </div>

      <div className="flex flex-1 flex-col gap-2 px-4 pb-3 pt-3">
        <div className="flex items-start justify-between gap-2">
          <h3 className="line-clamp-2 text-[14.5px] font-semibold leading-snug tracking-tight text-white">
            {item.title}
          </h3>
          {!isSoon && (
            <ArrowUpRight className="mt-0.5 h-3.5 w-3.5 shrink-0 text-white/90 transition-all duration-300 group-hover:translate-x-0.5 group-hover:text-white" />
          )}
        </div>
        {item.meta && (
          <p className="mt-auto truncate text-[11px] font-medium text-white/50">
            {item.meta}
          </p>
        )}
      </div>

      {!isSoon && (
        <div
          className="pointer-events-none absolute inset-0 rounded-xl opacity-0 transition-opacity duration-300 group-hover:opacity-100"
          style={{ boxShadow: `inset 0 0 0 1px rgba(${rgb},0.35)` }}
        />
      )}
    </motion.button>
  );
}

export function LibraryCard({
  item,
  onSelect,
}: {
  item: LibraryItem;
  onSelect?: () => void;
}) {
  if (item.kind === "skill") return <SkillCard item={item} onSelect={onSelect} />;
  if (item.kind === "signal" && item.signal)
    return <SignalCard item={item} onSelect={onSelect} />;
  if (item.kind === "video") return <VideoCard item={item} onSelect={onSelect} />;

  // Video items already early-return as <VideoCard> above, so by here item.kind
  // is never "video" — Icon from KIND_ICON covers every remaining kind.
  const Icon = KIND_ICON[item.kind];
  const rgb = hexToRgb(item.accent);
  const thumb = item.thumbnailUrl;

  return (
    <motion.button
      onClick={onSelect}
      whileHover={{ y: -4 }}
      transition={{ type: "spring", stiffness: 320, damping: 26 }}
      className="group relative flex h-full w-full flex-col overflow-hidden rounded-xl border border-white/[0.07] bg-[#0c0c0e] text-left shadow-[0_2px_14px_rgba(0,0,0,0.5)] transition-colors duration-300"
    >
      {/* 16:9 thumbnail (real image when present, else branded icon tile) */}
      <div className="relative aspect-[16/9] w-full overflow-hidden border-b border-white/[0.05]">
        {thumb ? (
          <Image
            src={thumb}
            alt={item.title}
            fill
            sizes="(max-width: 1024px) 50vw, 25vw"
            className="object-cover"
          />
        ) : (
          <>
            <div
              className="absolute inset-0"
              style={{
                background: `radial-gradient(120% 120% at 0% 0%, rgba(${rgb},0.16), transparent 55%), radial-gradient(130% 130% at 100% 100%, rgba(${rgb},0.10), transparent 50%), #0a0a0b`,
              }}
            />
            <div
              className="absolute inset-0 opacity-[0.06]"
              style={{
                backgroundImage:
                  "linear-gradient(to right, #fff 1px, transparent 1px), linear-gradient(to bottom, #fff 1px, transparent 1px)",
                backgroundSize: "26px 26px",
              }}
            />
            <div className="absolute inset-0 flex items-center justify-center">
              <div
                className="flex h-12 w-12 items-center justify-center rounded-xl border transition-transform duration-500 group-hover:scale-110"
                style={{
                  borderColor: `rgba(${rgb},0.30)`,
                  backgroundColor: `rgba(${rgb},0.10)`,
                }}
              >
                <Icon className="h-5 w-5" style={{ color: item.accent }} />
              </div>
            </div>
          </>
        )}
        {/* Automation thumbnails are GPT-generated full-bleed art that ALREADY
         *  bake their own asset chip + ALERT pill in the top corners, so an
         *  extra overlay tag just covers the title. Only show the overlay tag
         *  for the other kinds (idea/video) whose tiles have no baked label. */}
        {item.kind !== "automation" && (
          <div className="absolute left-2.5 top-2">
            <span
              className="rounded-md border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider backdrop-blur-md"
              style={{
                color: item.accent,
                borderColor: `rgba(${rgb},0.35)`,
                backgroundColor: "rgba(0,0,0,0.55)",
              }}
            >
              {item.tags?.[0] ?? KIND_LABEL[item.kind]}
            </span>
          </div>
        )}
      </div>

      {/* body — title + one meta row, same proportions as the other cards */}
      <div className="flex flex-1 flex-col gap-2 px-4 pb-3 pt-3">
        <div className="flex items-start justify-between gap-2">
          <h3 className="line-clamp-1 text-[14.5px] font-semibold leading-snug tracking-tight text-white">
            {item.title}
          </h3>
          <ArrowUpRight className="mt-0.5 h-3.5 w-3.5 shrink-0 text-white/90 transition-all duration-300 group-hover:translate-x-0.5 group-hover:text-white" />
        </div>
        {item.meta && (
          <p className="mt-auto truncate text-[11px] font-medium text-white/50">
            {item.meta}
          </p>
        )}
      </div>

      <div
        className="pointer-events-none absolute inset-0 rounded-xl opacity-0 transition-opacity duration-300 group-hover:opacity-100"
        style={{ boxShadow: `inset 0 0 0 1px rgba(${rgb},0.35)` }}
      />
    </motion.button>
  );
}
